import { ExternalBillingClaimStatus } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimStatus";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { ComboBoxDropDownItem } from "../Components/Inputs/Combobox";
import { PopulationBasedFundingStatusIcon } from "../Models/PopulationBasedFunding/PopulationBasedFundingStatusIcon";
import { AdministrativeCode, BatchEligibilityCheckStatusEnum, PopulationBasedFundingEventAction, PopulationBasedFundingEventDeregisteredReason, PopulationBasedFundingEventOverrideReason, PopulationBasedFundingProposalReason, PopulationBasedFundingStatus } from "../Models/UI/Enums";

class PopulationBasedFundingHelper {
    public static getEventActionVocabKey(event: PopulationBasedFundingEventWithNotesModel): string {
        if (event.ActionEnum === PopulationBasedFundingEventAction.Register) {
            if (event.isOverride)
                return "RegisteredOverrideLabel";
            else if (event.isPendingProposal)
                return "ProposedRegistrationLabel";
            else
                return "RegisteredLabel";
        }

        if (event.ActionEnum === PopulationBasedFundingEventAction.Deregister) {
            if (event.isOverride)
                return "DeregisteredOverrideLabel";
            else if (event.isPendingProposal)
                return "ProposedDeregistrationLabel";
            else
                return "DeregisteredLabel";
        }

        return "";
    }

    public static getEventActionText(event: PopulationBasedFundingEventWithNotesModel): string {
        if (Object.values(BatchEligibilityCheckStatusEnum).includes(event.BatchEligibilityCheckStatusEnum)) {
            const brcString = cacheStore.GetString("BatchRegistrationCheckAbbrevLabel");
            const valueString = cacheStore.GetString(`${BatchEligibilityCheckStatusEnum[event.BatchEligibilityCheckStatusEnum]}Label`);

            return `${brcString}: ${valueString}`;
        }

        let result = "";
        result = `${cacheStore.GetString(this.getEventActionVocabKey(event))}`;

        if (event.isAccepted)
            result = `${cacheStore.GetString("AcceptedLabel")}: ${result}`;

        switch (event.ClaimStatus) {
            case ExternalBillingClaimStatus.Submitted:
            case ExternalBillingClaimStatus.Held:
            case ExternalBillingClaimStatus.Unsubmitted:
                result = `${result} (${cacheStore.GetString("PendingLabel")})`;
                break;
            case ExternalBillingClaimStatus.Refused:
            case ExternalBillingClaimStatus.Rejected:
                result = `${result} (${cacheStore.GetString("RefusedLabel")})`;
                break;
        }

        return result;
    }

    public static getDeregisterReasonVocabKey(enumValue: PopulationBasedFundingEventDeregisteredReason): string {
        switch (enumValue) {
            case PopulationBasedFundingEventDeregisteredReason.AnotherReasonIndicatedInNotes:
                return "AnotherReasonIndicatedInNotesLabel";
            case PopulationBasedFundingEventDeregisteredReason.ChangePractitionerAssignment:
                return "PopulationBasedFundingChangePractitionerAssignmentLabel";
            case PopulationBasedFundingEventDeregisteredReason.Deceased:
                return "DeceasedLabel";
            case PopulationBasedFundingEventDeregisteredReason.LeftArea:
                return "PopulationBasedFundingLeftAreaLabel";
            case PopulationBasedFundingEventDeregisteredReason.ServicesReceivedOutsidePrimaryCareOrganization:
                return "PopulationBasedFundingServicesReceivedOutsidePrimaryCareOrganizationLabel";
            default:
                return null;
        }
    }

    public static getDeregistrationReasonsDropdownItems(): ComboBoxDropDownItem[] {
        const getDropdownItem = (reason: PopulationBasedFundingEventDeregisteredReason): ComboBoxDropDownItem =>
            new ComboBoxDropDownItem(reason.toString(), this.getDeregistrationReasonTranslation(reason));

        return [
            getDropdownItem(PopulationBasedFundingEventDeregisteredReason.Deceased),
            getDropdownItem(PopulationBasedFundingEventDeregisteredReason.ServicesReceivedOutsidePrimaryCareOrganization),
            getDropdownItem(PopulationBasedFundingEventDeregisteredReason.LeftArea),
            getDropdownItem(PopulationBasedFundingEventDeregisteredReason.ChangePractitionerAssignment),
            getDropdownItem(PopulationBasedFundingEventDeregisteredReason.AnotherReasonIndicatedInNotes)
        ];
    }

    public static getProposalReasonVocabKey(enumValue: PopulationBasedFundingProposalReason): string {
        if (!enumValue) return null;

        return `${PopulationBasedFundingProposalReason[enumValue]}Label`;
    }

    public static getOverrideReasonVocabKey(enumValue: PopulationBasedFundingEventOverrideReason): string {
        if (!enumValue) return null;

        if (enumValue == PopulationBasedFundingEventOverrideReason.AnotherRegistrationReasonIndicatedInNotes
            || enumValue == PopulationBasedFundingEventOverrideReason.AnotherDeregistrationReasonIndicatedInNotes) {
            return "AnotherReasonIndicatedInNotesLabel";
        }
        else {
            return `${PopulationBasedFundingEventOverrideReason[enumValue]}Label`;
        }
    }

    public static getStatusVocabKey(statusValue: PopulationBasedFundingStatus): string {
        return `${PopulationBasedFundingStatus[statusValue]}Label`;
    }

    public static getDeregisterReasonLabel(enumValue: PopulationBasedFundingEventDeregisteredReason): string {
        switch (enumValue) {
            case PopulationBasedFundingEventDeregisteredReason.AnotherReasonIndicatedInNotes:
                return "AnotherReasonIndicatedInNotesLabel";
            case PopulationBasedFundingEventDeregisteredReason.ChangePractitionerAssignment:
                return "PopulationBasedFundingChangePractitionerAssignmentLabel";
            case PopulationBasedFundingEventDeregisteredReason.Deceased:
                return "PopulationBasedFundingDeceasedLabel";
            case PopulationBasedFundingEventDeregisteredReason.LeftArea:
                return "PopulationBasedFundingLeftAreaLabel";
            case PopulationBasedFundingEventDeregisteredReason.ServicesReceivedOutsidePrimaryCareOrganization:
                return "PopulationBasedFundingServicesReceivedOutsidePrimaryCareOrganizationLabel";
            default:
                return null;
        }
    }

    public static getAdministrativeCodesDropdownItems(): ComboBoxDropDownItem[] {
        const getDropdownItem = (reason: AdministrativeCode): ComboBoxDropDownItem =>
            new ComboBoxDropDownItem(reason.toString(), `${reason.toString()} - ${cacheStore.GetString(`${AdministrativeCode[reason]}Label`)}`);

        return [
            getDropdownItem(AdministrativeCode.Normal),
            getDropdownItem(AdministrativeCode.SpecialNeeds)
        ];
    }

    public static getStatusIconFromBatchEligibilityStatusEnum(batchEligibilityCheckStatus: BatchEligibilityCheckStatusEnum): PopulationBasedFundingStatusIcon {
        switch (batchEligibilityCheckStatus) {
            case BatchEligibilityCheckStatusEnum.Registered:
                return PopulationBasedFundingStatusIcon.Registered;
            case BatchEligibilityCheckStatusEnum.NotRegistered:
                return PopulationBasedFundingStatusIcon.NotRegistered;
            case BatchEligibilityCheckStatusEnum.RegisteredToAnotherPbfPayee:
                return PopulationBasedFundingStatusIcon.RegisteredToAnotherPbfPayee;
            case BatchEligibilityCheckStatusEnum.PendingRegistration:
                return PopulationBasedFundingStatusIcon.PendingRegistration;
            case BatchEligibilityCheckStatusEnum.PendingDeregistration:
                return PopulationBasedFundingStatusIcon.PendingDeregistration;
            case BatchEligibilityCheckStatusEnum.RegisteredToAnotherPbfPayeeWithinTheSameReportingGroup:
                return PopulationBasedFundingStatusIcon.RegisteredToAnotherPbfPayeeWithinTheSameReportingGroup;
        }
    }

    public static determineStatusIcon(
        latestEventAction: PopulationBasedFundingEventAction,
        willContinueTreatment: boolean,
        isPendingOrOverriddenProposal: boolean,
        batchEligibilityCheckIconOverride: BatchEligibilityCheckStatusEnum,
        isRegisteredWithStarredFamilyPhysician: boolean
    ): PopulationBasedFundingStatusIcon {
        if (!!batchEligibilityCheckIconOverride)
            return PopulationBasedFundingHelper.getStatusIconFromBatchEligibilityStatusEnum(batchEligibilityCheckIconOverride);

        if (!latestEventAction)
            return PopulationBasedFundingStatusIcon.NotRegistered;

        if (isPendingOrOverriddenProposal) {
            if (willContinueTreatment)
                return PopulationBasedFundingStatusIcon.NotRegisteredButReceiveTreatment;
            else
                return latestEventAction === PopulationBasedFundingEventAction.Register
                    ? PopulationBasedFundingStatusIcon.NotRegistered
                    : PopulationBasedFundingStatusIcon.Registered;
        }

        if (latestEventAction === PopulationBasedFundingEventAction.Register) {
            return isRegisteredWithStarredFamilyPhysician
                ? PopulationBasedFundingStatusIcon.Registered
                : PopulationBasedFundingStatusIcon.RegisteredToDifferentPhysician;
        }

        if (latestEventAction === PopulationBasedFundingEventAction.Deregister) {
            return willContinueTreatment
                ? PopulationBasedFundingStatusIcon.NotRegisteredButReceiveTreatment
                : PopulationBasedFundingStatusIcon.NotRegistered;
        }
    }

    private static getDeregistrationReasonTranslation(reason: PopulationBasedFundingEventDeregisteredReason): string {
        const key = PopulationBasedFundingHelper.getDeregisterReasonLabel(reason);
        return cacheStore.GetString(key);
    }
}

export { PopulationBasedFundingHelper };
